import React, { useState, useEffect } from "react";
import { Typography, Radio, Textarea } from "@material-tailwind/react";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import LawyerFeedbackForm from "./LawyerFeedbackForm";
import { ValidationMessages } from "../utils/validationMessages";
import {
  formatInstructionReport,
  isInstructionReportValid,
  type InstructionReportForm,
  type YesNo,
} from "../utils/hearingReportValidation";

interface InstructionHearingReportProps {
  onChange: (reportData: any) => void;
  includeLawyerFeedback?: boolean;
}

const InstructionHearingReport: React.FC<InstructionHearingReportProps> = ({
  onChange,
  includeLawyerFeedback = true,
}) => {
  const [formData, setFormData] = useState<InstructionReportForm>({
    authorTestimony: null,
    hadConfession: null,
    confessionDescription: "",
    companyTestimony: null,
    otherDefendantTestimony: null,
    witnessTestimony: null,
    witnessEvaluation: "",
    companyExclusionRequested: null,
    observations: "",
  });

  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {}
  );
  const [isValid, setIsValid] = useState(false);
  const [lawyerFeedbackData, setLawyerFeedbackData] = useState<any>(null);

  useEffect(() => {
    const isFormValid = isInstructionReportValid(formData);
    setIsValid(
      includeLawyerFeedback
        ? isFormValid && !!lawyerFeedbackData?.isValid
        : isFormValid
    );
  }, [formData, includeLawyerFeedback, lawyerFeedbackData]);

  useEffect(() => {
    // Send both raw data and formatted data to parent
    const formattedBase = formatInstructionReport(formData);
    const formattedMerged = includeLawyerFeedback
      ? { ...formattedBase, ...lawyerFeedbackData?.formattedData }
      : formattedBase;

    if (!includeLawyerFeedback || lawyerFeedbackData) {
      onChange({
        ...formData,
        ...(includeLawyerFeedback ? { lawyerFeedbackData } : {}),
        isValid,
        formattedData: formattedMerged,
      });
    }
  }, [formData, isValid, lawyerFeedbackData, includeLawyerFeedback]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Mark field as touched
    setTouchedFields({
      ...touchedFields,
      [name]: true,
    });

    const newFormData = { ...formData, [name]: value };
    setFormData(newFormData);
  };

  const handleRadioChange = (name: keyof InstructionReportForm, value: YesNo) => {
    // Mark field as touched
    setTouchedFields({
      ...touchedFields,
      [name as string]: true,
    });

    // Reset dependent fields when parent field changes
    if (name === "authorTestimony" && value === "Não") {
      setFormData({
        ...formData,
        [name]: value,
        hadConfession: null,
        confessionDescription: "",
      });
    } else if (name === "hadConfession" && value === "Não") {
      setFormData({
        ...formData,
        [name]: value,
        confessionDescription: "",
      });
    } else if (name === "witnessTestimony" && value === "Não") {
      setFormData({
        ...formData,
        [name]: value,
        witnessEvaluation: "",
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Helper for field validation
  const isFieldInvalid = (fieldName: string): boolean => {
    if (!touchedFields[fieldName]) return false;
    return !formData[fieldName as keyof typeof formData];
  };

  // Required field marker
  const RequiredMark = () => <span className="text-red-500 ml-1">*</span>;

  return (
    <div className="space-y-4">
      {/* Author Testimony */}
      <div className="mb-4">
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          Houve depoimento do autor? <RequiredMark />
        </Typography>
        <div className="flex gap-4">
          <Radio
            name="authorTestimony"
            label="Sim"
            onChange={() => handleRadioChange("authorTestimony", "Sim")}
            checked={formData.authorTestimony === "Sim"}
          />
          <Radio
            name="authorTestimony"
            label="Não"
            onChange={() => handleRadioChange("authorTestimony", "Não")}
            checked={formData.authorTestimony === "Não"}
          />
        </div>
        {isFieldInvalid("authorTestimony") && (
          <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded mt-2">
            <ExclamationCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <Typography className="text-red-700 text-xs leading-relaxed" placeholder="">
              {ValidationMessages.HEARING_REPORT.AUTHOR_TESTIMONY_REQUIRED}
            </Typography>
          </div>
        )}
      </div>

      {/* Confession - Only show if there was author testimony */}
      {formData.authorTestimony === "Sim" && (
        <div className="mb-4">
          <Typography variant="small" className="text-blue-700 font-medium mb-1">
            Houve confissão de algum fato? <RequiredMark />
          </Typography>
          <div className="flex gap-4">
            <Radio
              name="hadConfession"
              label="Sim"
              onChange={() => handleRadioChange("hadConfession", "Sim")}
              checked={formData.hadConfession === "Sim"}
            />
            <Radio
              name="hadConfession"
              label="Não"
              onChange={() => handleRadioChange("hadConfession", "Não")}
              checked={formData.hadConfession === "Não"}
            />
          </div>
          {isFieldInvalid("hadConfession") && (
            <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded mt-2">
              <ExclamationCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <Typography className="text-red-700 text-xs leading-relaxed" placeholder="">
                {ValidationMessages.HEARING_REPORT.CONFESSION_REQUIRED}
              </Typography>
            </div>
          )}
        </div>
      )}

      {/* Confession Description - Only show if there was a confession */}
      {formData.hadConfession === "Sim" && (
        <div className="mb-4">
          <Typography
            variant="small"
            className="text-blue-700 font-medium mb-1"
          >
            Descrição do fato confessado <RequiredMark />
          </Typography>
          <Textarea
            name="confessionDescription"
            value={formData.confessionDescription}
            onChange={handleInputChange}
            maxLength={1000}
            className={`w-full ${
              isFieldInvalid("confessionDescription") ? "border-red-500" : ""
            }`}
            placeholder="Descreva o fato confessado em detalhes (mínimo 50 caracteres)"
          />
          <Typography className={`text-xs text-right mt-1 ${formData.confessionDescription.length >= 1000 ? 'text-red-500 font-medium' : 'text-gray-400'}`} placeholder="">
            {formData.confessionDescription.length}/1000 caracteres
          </Typography>
          {isFieldInvalid("confessionDescription") && (
            <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded mt-2">
              <ExclamationCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <Typography className="text-red-700 text-xs leading-relaxed" placeholder="">
                {ValidationMessages.HEARING_REPORT.CONFESSION_DESCRIPTION_REQUIRED}
              </Typography>
            </div>
          )}
          {formData.confessionDescription.length < 50 &&
            touchedFields["confessionDescription"] && (
              <div className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded mt-2">
                <ExclamationCircleIcon className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <Typography className="text-yellow-700 text-xs leading-relaxed" placeholder="">
                  ⏳ {formData.confessionDescription.length}/50 caracteres mínimos
                </Typography>
              </div>
            )}
        </div>
      )}

      {/* Company Testimony */}
      <div className="mb-4">
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          Houve depoimento da empresa/preposto? <RequiredMark />
        </Typography>
        <div className="flex gap-4">
          <Radio
            name="companyTestimony"
            label="Sim"
            onChange={() => handleRadioChange("companyTestimony", "Sim")}
            checked={formData.companyTestimony === "Sim"}
          />
          <Radio
            name="companyTestimony"
            label="Não"
            onChange={() => handleRadioChange("companyTestimony", "Não")}
            checked={formData.companyTestimony === "Não"}
          />
        </div>
        {isFieldInvalid("companyTestimony") && (
          <Typography className="text-red-500 text-xs mt-1">
            Este campo é obrigatório
          </Typography>
        )}
      </div>

      {/* Other Defendant Testimony */}
      <div className="mb-4">
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          Houve depoimento de outro réu/reclamada? <RequiredMark />
        </Typography>
        <div className="flex gap-4">
          <Radio
            name="otherDefendantTestimony"
            label="Sim"
            onChange={() => handleRadioChange("otherDefendantTestimony", "Sim")}
            checked={formData.otherDefendantTestimony === "Sim"}
          />
          <Radio
            name="otherDefendantTestimony"
            label="Não"
            onChange={() => handleRadioChange("otherDefendantTestimony", "Não")}
            checked={formData.otherDefendantTestimony === "Não"}
          />
        </div>
        {isFieldInvalid("otherDefendantTestimony") && (
          <Typography className="text-red-500 text-xs mt-1">
            Este campo é obrigatório
          </Typography>
        )}
      </div>

      {/* Witness Testimony */}
      <div className="mb-4">
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          Houve depoimento de testemunhas? <RequiredMark />
        </Typography>
        <div className="flex gap-4">
          <Radio
            name="witnessTestimony"
            label="Sim"
            onChange={() => handleRadioChange("witnessTestimony", "Sim")}
            checked={formData.witnessTestimony === "Sim"}
          />
          <Radio
            name="witnessTestimony"
            label="Não"
            onChange={() => handleRadioChange("witnessTestimony", "Não")}
            checked={formData.witnessTestimony === "Não"}
          />
        </div>
        {isFieldInvalid("witnessTestimony") && (
          <Typography className="text-red-500 text-xs mt-1">
            Este campo é obrigatório
          </Typography>
        )}
      </div>

      {/* Witness Evaluation - Only show if there were witnesses */}
      {formData.witnessTestimony === "Sim" && (
        <div className="mb-4">
          <Typography
            variant="small"
            className="text-blue-700 font-medium mb-1"
          >
            No seu entendimento, as testemunhas foram favoráveis ou contrárias à
            empresa? <RequiredMark />
          </Typography>
          <Textarea
            name="witnessEvaluation"
            value={formData.witnessEvaluation}
            onChange={handleInputChange}
            maxLength={1000}
            className={`w-full ${
              isFieldInvalid("witnessEvaluation") ? "border-red-500" : ""
            }`}
            placeholder="Descreva sua avaliação do depoimento das testemunhas (mínimo 50 caracteres)"
          />
          <Typography className={`text-xs text-right mt-1 ${formData.witnessEvaluation.length >= 1000 ? 'text-red-500 font-medium' : 'text-gray-400'}`} placeholder="">
            {formData.witnessEvaluation.length}/1000 caracteres
          </Typography>
          {isFieldInvalid("witnessEvaluation") && (
            <Typography className="text-red-500 text-xs mt-1">
              Este campo é obrigatório (mínimo 50 caracteres)
            </Typography>
          )}
          {formData.witnessEvaluation.length < 50 &&
            touchedFields["witnessEvaluation"] && (
              <Typography className="text-red-500 text-xs mt-1">
                A avaliação deve ter pelo menos 50 caracteres (atual:{" "}
                {formData.witnessEvaluation.length})
              </Typography>
            )}
        </div>
      )}

      {/* Add Company Exclusion Request field */}
      <div className="mb-4">
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          Foi solicitada a exclusão da empresa do processo? <RequiredMark />
        </Typography>
        <div className="flex gap-4">
          <Radio
            name="companyExclusionRequested"
            label="Sim"
            onChange={() =>
              handleRadioChange("companyExclusionRequested", "Sim")
            }
            checked={formData.companyExclusionRequested === "Sim"}
          />
          <Radio
            name="companyExclusionRequested"
            label="Não"
            onChange={() =>
              handleRadioChange("companyExclusionRequested", "Não")
            }
            checked={formData.companyExclusionRequested === "Não"}
          />
        </div>
        {isFieldInvalid("companyExclusionRequested") && (
          <Typography className="text-red-500 text-xs mt-1">
            Este campo é obrigatório
          </Typography>
        )}
      </div>

      {/* General Observations */}
      <div className="mb-4">
        <Typography variant="small" className="text-blue-700 font-medium mb-1">
          Observações gerais (visível para a empresa contratante)
        </Typography>
        <Textarea
          name="observations"
          value={formData.observations}
          onChange={handleInputChange}
          maxLength={1000}
          className="w-full"
          placeholder="Observações adicionais sobre a audiência"
        />
        <Typography className={`text-xs text-right mt-1 ${formData.observations.length >= 1000 ? 'text-red-500 font-medium' : 'text-gray-400'}`} placeholder="">
          {formData.observations.length}/1000 caracteres
        </Typography>
      </div>

      {/* Lawyer Feedback Section (optional) */}
      {includeLawyerFeedback && (
        <div className="mt-6 border-t pt-6">
          <LawyerFeedbackForm onChange={setLawyerFeedbackData} />
        </div>
      )}

      <Typography className="text-xs text-gray-500 mt-4">
        Os campos marcados com <span className="text-red-500">*</span> são
        obrigatórios
      </Typography>
    </div>
  );
};

export default InstructionHearingReport;
